/**
 * ESC/POS over WebUSB (Android Chrome / SUNMI, USB cable) — the simplest path (design/05 §6).
 * The first connection needs a tap in settings (`connect()` → the browser's device chooser);
 * afterwards `print()` finds the remembered printer with `getDevices()` by itself.
 * Written directly on the WebUSB API (bulk OUT endpoint) — no library needed.
 */
import { DRAWER_KICK, chunks } from "./escpos";
import { navigatorHardware, type UsbDeviceLike, type UsbLike } from "./hardware";
import { PrinterError, type PrinterDriver, type PrinterStatus } from "./types";

/** USB class 7 = printer; vendors of the models sold in Japan (Epson, Star, Xprinter/Zjiang, SUNMI). */
export const USB_FILTERS: Array<Record<string, number>> = [
  { classCode: 7 },
  { vendorId: 0x04b8 }, // Seiko Epson
  { vendorId: 0x0519 }, // Star Micronics
  { vendorId: 0x0483 }, // STMicro (Xprinter, many OEM)
  { vendorId: 0x0416 }, // Winbond (Xprinter / Zjiang)
  { vendorId: 0x1fc9 }, // NXP (OEM printers)
  { vendorId: 0x28e9 }, // GigaDevice (OEM printers)
];

const USB_CHUNK = 16 * 1024;

interface Link {
  device: UsbDeviceLike;
  endpoint: number;
}

/**
 * Which USB printer this driver prints to. A counter often has two on the same tablet (the
 * receipt printer + the label printer — T07-34/35 bench): without it, `getDevices()[0]` could
 * send a receipt to the label printer. Remembered when the staff picks the printer in 設定.
 */
export interface UsbIdentity {
  vendorId: number;
  productId: number;
  serialNumber?: string | null;
}

export function usbIdentityOf(device: UsbDeviceLike): UsbIdentity {
  return {
    vendorId: device.vendorId,
    productId: device.productId,
    serialNumber: device.serialNumber ?? null,
  };
}

export function sameUsbDevice(device: UsbDeviceLike, identity: UsbIdentity): boolean {
  return (
    device.vendorId === identity.vendorId &&
    device.productId === identity.productId &&
    (!identity.serialNumber || (device.serialNumber ?? null) === identity.serialNumber)
  );
}

export interface WebUsbOptions {
  /** The printer chosen in settings (null = the first remembered one — a single printer). */
  device?: UsbIdentity | null;
  /** Called after the staff picked a printer in the browser's chooser (to remember it). */
  onChosen?: (identity: UsbIdentity) => void;
}

export class WebUsbPrinter implements PrinterDriver {
  readonly kind = "webusb" as const;
  private link: Link | null = null;
  private readonly usb: () => UsbLike | undefined;
  private readonly options: WebUsbOptions;

  constructor(
    usb: () => UsbLike | undefined = () => navigatorHardware().usb,
    options: WebUsbOptions = {},
  ) {
    this.usb = usb;
    this.options = options;
  }

  private pick(known: UsbDeviceLike[]): UsbDeviceLike | undefined {
    const wanted = this.options.device;
    return wanted ? known.find((d) => sameUsbDevice(d, wanted)) : known[0];
  }

  supported(): boolean {
    return this.usb() !== undefined;
  }

  private usbOrThrow(): UsbLike {
    const usb = this.usb();
    if (!usb) {
      throw new PrinterError("not_supported");
    }
    return usb;
  }

  async connect(): Promise<PrinterStatus> {
    const usb = this.usbOrThrow();
    let device: UsbDeviceLike;
    try {
      device = await usb.requestDevice({ filters: USB_FILTERS });
    } catch (cause) {
      throw new PrinterError("not_found", cause instanceof Error ? cause.message : undefined);
    }
    this.link = await this.open(device);
    this.options.onChosen?.(usbIdentityOf(device));
    return this.status();
  }

  private async open(device: UsbDeviceLike): Promise<Link> {
    try {
      if (!device.opened) {
        await device.open();
      }
      if (device.configuration === null) {
        const first = device.configurations?.[0]?.configurationValue ?? 1;
        await device.selectConfiguration(first);
      }
      const interfaces = device.configuration?.interfaces ?? [];
      for (const iface of interfaces) {
        const alternates = iface.alternates?.length ? iface.alternates : [iface.alternate];
        for (const alt of alternates) {
          const out = alt.endpoints.find((e) => e.direction === "out" && e.type === "bulk");
          if (out) {
            if (!iface.claimed) {
              await device.claimInterface(iface.interfaceNumber);
            }
            return { device, endpoint: out.endpointNumber };
          }
        }
      }
    } catch (cause) {
      throw new PrinterError("disconnected", cause instanceof Error ? cause.message : undefined);
    }
    throw new PrinterError("not_found", "no bulk OUT endpoint");
  }

  private async ensure(): Promise<Link> {
    if (this.link?.device.opened) {
      return this.link;
    }
    const usb = this.usbOrThrow();
    const device = this.pick(await usb.getDevices());
    if (!device) {
      throw new PrinterError("not_found");
    }
    this.link = await this.open(device);
    return this.link;
  }

  async print(bytes: Uint8Array): Promise<void> {
    const link = await this.ensure();
    for (const part of chunks(bytes, USB_CHUNK)) {
      let result: { status: string; bytesWritten: number };
      try {
        result = await link.device.transferOut(link.endpoint, part);
      } catch (cause) {
        this.link = null;
        throw new PrinterError("disconnected", cause instanceof Error ? cause.message : undefined);
      }
      if (result.status !== "ok" || result.bytesWritten !== part.length) {
        throw new PrinterError("write_failed", result.status);
      }
    }
  }

  async openDrawer(): Promise<void> {
    await this.print(DRAWER_KICK);
  }

  async status(): Promise<PrinterStatus> {
    const device = this.link?.device;
    if (!device) {
      const known = this.supported() ? await this.usbOrThrow().getDevices() : [];
      const remembered = this.pick(known);
      return {
        connected: false,
        name: remembered?.productName ?? null,
        error: remembered ? null : "not_found",
      };
    }
    return {
      connected: device.opened,
      name: [device.manufacturerName, device.productName].filter(Boolean).join(" ") || null,
      error: device.opened ? null : "disconnected",
    };
  }

  async disconnect(): Promise<void> {
    const device = this.link?.device;
    this.link = null;
    if (device?.opened) {
      await device.close();
    }
  }
}
